// src/components/hero/Hero.jsx
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import SectionWrapper from '../layout/SectionWrapper';
import noiseTexture from '../../assets/noise.svg';
import avatarSketch from '../../assets/avatar-sketch.png';

const logicTree = [
  "Code like architecture. Scale like biology.",
  "IF problem → map constraints",
  "THEN architect scalable backend",
  "ELSE iterate fast with feedback",
];

export default function Hero() {
  const glowRef = useRef(null);
  const parallaxRef = useRef(null);
  const [visibleLine, setVisibleLine] = useState(0);
  const [typedText, setTypedText] = useState('');

  useEffect(() => {
    if (!logicTree.length) return;
  
    const fullLine = logicTree[visibleLine];
    if (!fullLine) return; // defensive
  
    let charIndex = -1;
    setTypedText('');
    const typingInterval = setInterval(() => {
      charIndex++;
      if (charIndex < fullLine.length) {
        setTypedText((prev) => prev + fullLine[charIndex]);
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setVisibleLine((prev) => (prev + 1) % logicTree.length);
        }, 2000);
      }
    }, 60);
  
    return () => clearInterval(typingInterval);
  }, [visibleLine]);
  

  useEffect(() => {
    const handleMove = (e) => {
      const { innerWidth, innerHeight } = window;
      const x = e.clientX / innerWidth - 0.5;
      const y = e.clientY / innerHeight - 0.5;

      if (glowRef.current) {
        glowRef.current.style.top = `${e.clientY}px`;
        glowRef.current.style.left = `${e.clientX}px`;
        glowRef.current.style.transform = `translate(-50%, -50%)`;
      }

      if (parallaxRef.current) {
        parallaxRef.current.style.transform = `translate3d(${x * 40}px, ${y * 40}px, 0)`;
      }
    };
    document.addEventListener('mousemove', handleMove);
    return () => document.removeEventListener('mousemove', handleMove);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText('[ Aniket@portfolio ~ ]# tail -f /current-focus → AI Infra');
  };

  return (
    <SectionWrapper
      id="home"
      className="relative min-h-[90vh] flex items-start justify-start px-8 pt-28 overflow-hidden"
    >
      {/* Noise overlay */}
      <div className="absolute inset-0 z-0 opacity-[0.05]" style={{ backgroundImage: `url(${noiseTexture})` }}></div>

      {/* Avatar Sketch */}
      <img
        src={avatarSketch}
        alt="Aniket silhouette"
        className="absolute bottom-0 right-0 h-64 opacity-5 select-none pointer-events-none"
      />

      {/* Parallax Layer */}
      <div ref={parallaxRef} className="absolute inset-0 z-0 will-change-transform">
        <div className="absolute top-[25%] left-[15%] w-[500px] h-[500px] bg-gradient-to-br from-purple-700 via-transparent to-purple-900 opacity-25 rounded-full blur-xl" />
        <div className="absolute bottom-[15%] right-[20%] w-[400px] h-[400px] bg-gradient-to-bl from-violet-500 via-transparent to-violet-800 opacity-25 rounded-full blur-lg" />
      </div>

      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-3xl z-10"
      >
        <h1 className="text-left text-4xl sm:text-5xl md:text-6xl font-bold font-geist tracking-tight text-slate-100 mb-4">
          Systems &gt; Aesthetics
        </h1>

        <div className="bg-black/30 backdrop-blur-lg px-6 py-4 rounded-lg border border-white/10 text-left font-mono text-sm text-purple-300 shadow-lg h-[100px] flex items-center relative group">
          <motion.div
            key={visibleLine}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="leading-relaxed whitespace-nowrap overflow-hidden border-r-2 border-purple-400 pr-1"
          >
            <span className="text-purple-300">{typedText}</span>
            <span className="animate-pulse">▮</span>
          </motion.div>
        </div>

        <motion.p
          className="mt-6 text-slate-400 font-inter text-lg sm:text-xl"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.8 }}
        >
          Full Stack Engineer · Spring Boot · Flask · Javascript · Docker 
        </motion.p>

        <motion.div
          className="mt-8 flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.6 }}
        >
          <a
            href="#projects"
            className="px-6 py-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-lg transition"
          >
            See how I think
          </a>
          <a
  href="/resume.pdf"
  download="Aniket_Shukla_Resume.pdf"
  className="px-6 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-violet-400 font-semibold border border-violet-400 shadow"
>
  Resume
</a>
        </motion.div>
      </motion.div>

      {/* Custom cursor-follow glow */}
      <div
        ref={glowRef}
        className="pointer-events-none fixed top-0 left-0 z-50 h-28 w-28 bg-violet-500 opacity-30 rounded-full blur-[70px] mix-blend-screen animate-pulse transition-all duration-100"
        style={{ transform: 'translate(-50%, -50%)' }}
      ></div>

      {/* Terminal tag with click-to-copy */}
      <motion.div
        onClick={handleCopy}
        className="absolute bottom-6 left-6 text-xs text-slate-500 font-mono opacity-60 hover:opacity-100 hover:text-cyan-300 cursor-pointer transition"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.8 }}
      >
        [ Aniket@portfolio ~ ]# tail -f /current-focus → AI Infra
      </motion.div>
    </SectionWrapper>
  );
}